import './index.css'
import cn from 'clsx'

import React, { useCallback } from 'react'
type Props = {
  checkboxRef?: (...args: Array<any>) => any
  checked?: boolean | null | undefined
  children?: React.ReactNode
  className?: string
  defaultChecked?: boolean
  disabled?: boolean
  label?: React.ReactNode
  name?: string
  // TODO: Remove partial prop in favor of {checked: boolean | "indeterminate"}
  partial?: boolean
  onChange?:
    | ((
        label: React.ReactNode | null | undefined,
        checked: boolean,
        event: React.SyntheticEvent<HTMLInputElement>,
      ) => unknown)
    | null
    | undefined
}

/**
 * @deprecated Use `Checkbox` from `components/ds` instead.
 * @see {@link components/ds/Checkbox}
 * @migration
 * - Replace `<Checkbox>` with `<Checkbox>` from components/ds
 * - Prop mapping:
 *   - checked → checked (same)
 *   - disabled → disabled (same)
 *   - partial → checked="indeterminate"
 *   - onChange(label, checked, event) → onCheckedChange(checked: boolean | "indeterminate")
 *   - label → Use `<Text as="label">` composition with DS Checkbox
 *   - name → Use with form libraries or wrap in form element
 * - DS Checkbox does not support children; compose with other elements as needed
 * - className/checkboxRef have no direct DS equivalent; use wrapper component if needed
 *
 * A customizable checkbox component with support for indeterminate states, labels, and custom styling
 * @param checkboxRef - Optional ref callback for the checkbox input element
 * @param checked - The checked state of the checkbox (boolean, null, or undefined)
 * @param children - Additional content to render alongside the label
 * @param className - Additional CSS classes to apply to the checkbox container
 * @param defaultChecked - The default checked state (uncontrolled component) - defaults to false
 * @param disabled - Whether the checkbox is disabled - defaults to false
 * @param label - The label text or element to display next to the checkbox
 * @param name - The name attribute for the checkbox input
 * @param partial - Whether to show an indeterminate/partial state (dash instead of checkmark) - defaults to false
 * @param onChange - Callback function called when checkbox state changes, receives (label, checked, event)
 * @returns A label element containing the checkbox input and associated content
 */
const Checkbox = ({
  checkboxRef,
  checked,
  children,
  className,
  defaultChecked,
  disabled,
  label,
  name,
  partial,
  onChange,
}: Props): React.ReactElement<'label'> => {
  const inputProps = {
    checked: checked ?? undefined,
    defaultChecked,
    disabled,
    name,
  }
  const handleChange = useCallback(
    (event: React.SyntheticEvent<HTMLInputElement>) => {
      if (onChange) {
        onChange(label, !checked, event)
      }
    },
    [checked, label, onChange],
  )
  const stopPropagation = useCallback(
    (event: React.MouseEvent<HTMLLabelElement>) => {
      event.stopPropagation()
    },
    [],
  )
  return (
    <label
      className={cn('Checkbox', className, {
        'Checkbox--disabled': disabled,
      })}
      onClick={onChange ? stopPropagation : undefined}
    >
      <input
        className="Checkbox-input" // TODO: Check if readOnly is required
        readOnly={disabled || !onChange}
        ref={checkboxRef}
        type="checkbox"
        onChange={!disabled ? handleChange : undefined}
        {...inputProps}
      />
      <div className="Checkbox-toggle">
        <svg
          className="Checkbox-toggle-icon"
          fill="currentcolor"
          width="16"
          height="16"
        >
          {partial ? (
            <rect rx="1" x="3" y="7" width="10" height="2" />
          ) : (
            <path d="M6.31475766 11.8585l-3.18180372-3.417c-.17727192-.188-.17727192-.494 0-.684l.64272435-.683c.17727192-.188.46545243-.188.64272435 0l2.21817174 2.392 4.94452302-5.325c.1772719-.188.4654524-.188.6427243 0l.6427244.684c.1772719.188.1772719.495 0 .682l-5.90906409 6.351c-.17727192.188-.46545243.188-.64272435 0" />
          )}
        </svg>
      </div>
      {(label || children) && (
        <div className="Checkbox-label">
          {label}
          {children}
        </div>
      )}
    </label>
  )
}

export default Checkbox
